import { writable, derived } from 'svelte/store';

/**
 * Settings Store
 * Manages application-wide settings and preferences
 */

// Default settings
const defaultSettings = {
    claims: {
        // Autonumbering for subnumbers (a), (b), (c) and (i), (ii), (iii)
        // Main claim numbers (1., 2., 3.) are always autonumbered
        autoNumberSubitems: true,
        // Automatically update claim dependencies when refreshing terms
        autoUpdateDependencies: false
    },
    terms: {
        // Automatically refresh terms when claims editor content changes
        autoRefresh: false
    },
    personalization: {
        // Invention disclosure form text
        idfText: '',
        // Custom text styles for Word export
        customStyles: {
            style1: {
                delimiter: '{{ }}',
                color: '#859BFF',
                bold: false,
                italic: false,
                underline: false
            },
            style2: {
                delimiter: '// //',
                color: '#D4548A',
                bold: false,
                italic: false,
                underline: false
            }
        }
    },
    // Patent application settings
    patentType: 'provisional',
    jurisdiction: 'US'
};

// Load settings from localStorage if available
function loadSettings() {
    if (typeof window === 'undefined') return defaultSettings;

    try {
        const stored = localStorage.getItem('patdraft-settings');
        if (stored) {
            const parsed = JSON.parse(stored);
            // Merge with defaults to ensure all settings exist
            return {
                ...defaultSettings,
                ...parsed,
                claims: {
                    ...defaultSettings.claims,
                    ...(parsed.claims || {})
                },
                terms: {
                    ...defaultSettings.terms,
                    ...(parsed.terms || {})
                },
                personalization: {
                    ...defaultSettings.personalization,
                    ...(parsed.personalization || {}),
                    customStyles: {
                        ...defaultSettings.personalization.customStyles,
                        ...(parsed.personalization?.customStyles || {})
                    }
                },
                // Always reset patent type to provisional on page refresh
                patentType: 'provisional',
                jurisdiction: 'US'
            };
        }
    } catch (error) {
        console.error('Failed to load settings:', error);
    }

    return defaultSettings;
}

// Load settings from backend and merge with local
async function loadSettingsFromBackend() {
    if (typeof window === 'undefined') return;

    try {
        console.log('[SettingsStore] Loading settings from backend...');
        const response = await fetch('/api/user/profile', {
            credentials: 'include'
        });

        if (response.ok) {
            const data = await response.json();
            console.log('[SettingsStore] Backend response:', data);

            if (data.success && data.profile && data.profile.settings) {
                // Merge backend settings with local settings
                const backendSettings = data.profile.settings;
                const localSettings = loadSettings();

                console.log('[SettingsStore] Backend settings:', backendSettings);
                console.log('[SettingsStore] Local settings:', localSettings);

                // Backend settings take precedence
                const mergedSettings = {
                    ...localSettings,
                    ...backendSettings,
                    claims: {
                        ...localSettings.claims,
                        ...(backendSettings.claims || {})
                    },
                    terms: {
                        ...localSettings.terms,
                        ...(backendSettings.terms || {})
                    },
                    personalization: {
                        ...localSettings.personalization,
                        ...(backendSettings.personalization || {}),
                        customStyles: {
                            ...localSettings.personalization.customStyles,
                            ...(backendSettings.personalization?.customStyles || {})
                        }
                    },
                    // Always reset patent type to provisional on page refresh
                    patentType: 'provisional',
                    jurisdiction: 'US'
                };

                console.log('[SettingsStore] Merged settings:', mergedSettings);

                // Save merged settings to localStorage
                localStorage.setItem('patdraft-settings', JSON.stringify(mergedSettings));

                return mergedSettings;
            }
        } else {
            console.log('[SettingsStore] Backend response not OK:', response.status);
        }
    } catch (error) {
        console.log('[SettingsStore] Could not load settings from backend, using local settings:', error);
    }

    return loadSettings();
}

// Create the writable store
function createSettingsStore() {
    const { subscribe, set, update: svelteUpdate } = writable(loadSettings());
    
    return {
        subscribe,
        set,
        update(fn) {
            svelteUpdate(settings => {
                const newSettings = fn(settings);
                // Save to localStorage
                if (typeof window !== 'undefined') {
                    try {
                        localStorage.setItem('patdraft-settings', JSON.stringify(newSettings));
                    } catch (error) {
                        console.error('Failed to save settings:', error);
                    }
                }
                return newSettings;
            });
        },
        
        // Update a specific setting
        updateSetting(path, value) {
            svelteUpdate(settings => {
                const keys = path.split('.');
                const newSettings = { ...settings };
                let current = newSettings;
                
                // Navigate to the nested property
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
                
                // Set the value
                current[keys[keys.length - 1]] = value;
                
                // Save to localStorage
                if (typeof window !== 'undefined') {
                    try {
                        localStorage.setItem('patdraft-settings', JSON.stringify(newSettings));
                    } catch (error) {
                        console.error('Failed to save settings:', error);
                    }
                }
                
                return newSettings;
            });
        },
        
        // Toggle a boolean setting
        toggleSetting(path) {
            svelteUpdate(settings => {
                const keys = path.split('.');
                const newSettings = { ...settings };
                let current = newSettings;
                
                // Navigate to the nested property
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
                
                // Toggle the boolean value
                const lastKey = keys[keys.length - 1];
                const oldValue = current[lastKey];
                current[lastKey] = !current[lastKey];
                
                console.log('[SettingsStore] Toggled setting:', {
                    path,
                    oldValue,
                    newValue: current[lastKey],
                    fullSettings: newSettings
                });
                
                // Save to localStorage
                if (typeof window !== 'undefined') {
                    try {
                        localStorage.setItem('patdraft-settings', JSON.stringify(newSettings));
                        console.log('[SettingsStore] Saved to localStorage');
                    } catch (error) {
                        console.error('Failed to save settings:', error);
                    }
                }
                
                return newSettings;
            });
        },
        
        // Reset to defaults
        reset() {
            set(defaultSettings);
            if (typeof window !== 'undefined') {
                try {
                    localStorage.removeItem('patdraft-settings');
                } catch (error) {
                    console.error('Failed to reset settings:', error);
                }
            }
        },

        // Load settings from backend
        async loadFromBackend() {
            console.log('[SettingsStore] loadFromBackend() called');
            const settings = await loadSettingsFromBackend();
            if (settings) {
                console.log('[SettingsStore] Setting store with backend settings:', settings);
                set(settings);
            } else {
                console.log('[SettingsStore] No settings returned from backend');
            }
        }
    };
}

// Export the store instance
export const settingsStore = createSettingsStore();

// Export derived stores for specific settings sections
export const claimsSettings = derived(
    settingsStore,
    $settings => $settings.claims
);

export const termsSettings = derived(
    settingsStore,
    $settings => $settings.terms
);

export const personalizationSettings = derived(
    settingsStore,
    $settings => $settings.personalization
);